import {useCallback, useEffect, useRef, useState} from 'react';
import ClickAwayListener from 'react-click-away-listener';
import {Button, Icon} from '@ezcater/tapas';
import {faMagnifyingGlass} from '@fortawesome/pro-regular-svg-icons';
import {useReducedMotion} from 'motion/react';
import useTranslation from 'next-translate/useTranslation';
import {twJoin, twMerge} from 'tailwind-merge';

import useEventBarFastFollows from '@/components/global/layout/AppBar/hooks/useEventBarFastFollows';
import {useStoreNextRouter} from '@/components/global/providers/StoreNextRouter';
import useIdentity from '@/hooks/useIdentity';
import useMediaQuery from '@/hooks/useMediaQuery';
import usePreciseAddressNudge from '@/hooks/usePreciseAddressNudge';
import {appBarMedia} from '@/styles/appBarMq';
import {useCommitEventBarFulfillment} from './hooks/useCommitEventBarFulfillment';
import {useEventBarAskFlow} from './hooks/useEventBarAskFlow';
import {type SubmitSearchOverride, useEventBarV2Search} from './hooks/useEventBarV2Search';
import {useEventBarV2Tracking} from './hooks/useEventBarV2Tracking';
import {useMenuFulfillmentPending} from './hooks/useMenuFulfillmentPending';
import useRevertStagedOnFilterSearch from './hooks/useRevertStagedOnFilterSearch';
import {type AdvanceDecision} from './utils/askFlow';
import {areAdjacentFields, type EventBarField, fieldFromEventTarget} from './utils/fields';
import What from './What/What';
import When from './When/When';
import Where, {type SearchGuardResult} from './Where/Where';
import Who from './Who/Who';
import {
  CELL_RESIZE_TRANSITION_CLASSES,
  PILL_FADE_DURATION_MS,
  PILL_SLIDE_DURATION_MS,
  SEARCH_BUTTON_TRANSITION_CLASSES,
} from './animation';
import {useEventBarV2} from './EventBarV2Provider';
import FocusPill from './FocusPill';
import MenuUpdateConfirmationModal from './MenuUpdateConfirmationModal';
import {recordRecentlyUsedAddress} from './recentlyUsedAddresses';

type DividerProps = {
  isCompressed?: boolean;
  isHidden?: boolean;
};

const Divider: React.FC<DividerProps> = ({isCompressed = false, isHidden = false}) => {
  return (
    <div
      // `:is(:hover,:focus-within,[data-pill-connected])` == "an active field": hovered, focused, or
      // held connected while the pill slides to/from it. Hide the line next to any active field, but
      // fill the gap when it sits *between* two of them so their pills merge into one shape.
      className={twMerge(
        'flex w-2 shrink-0 items-center justify-center self-stretch',
        '[&:has(+_.peer:is(:hover,:focus-within,[data-pill-connected]))]:invisible [.peer:is(:hover,:focus-within,[data-pill-connected])_+_&]:invisible',
        // !visible beats the invisibility above (incl. the popup-driven isHidden).
        '[.peer:is(:hover,:focus-within,[data-pill-connected])_+_&:has(+_.peer:is(:hover,:focus-within,[data-pill-connected]))]:!visible [.peer:is(:hover,:focus-within,[data-pill-connected])_+_&:has(+_.peer:is(:hover,:focus-within,[data-pill-connected]))]:bg-peppercorn-50',
        isHidden && 'invisible',
      )}
    >
      <div
        className={twJoin(
          'w-px bg-peppercorn-100 [.peer:is(:hover,:focus-within,[data-pill-connected])_+_div:has(+_.peer:is(:hover,:focus-within,[data-pill-connected]))_>_&]:bg-peppercorn-50',
          isCompressed ? 'h-5' : 'h-12',
        )}
      />
    </div>
  );
};

type EventBarV2Props = {
  className?: string;
  isCompressed?: boolean;
  // Whether this instance is the one currently on-screen. Only the visible instance acts on
  // external openField() commands, so the compressed and expanded bars never cross. Defaults to
  // true so a standalone bar (stories, non-search pages) is interactive.
  isVisible?: boolean;
  // FDIC: collapse the expanded overlay after a successful field Update.
  onUpdateApplied?: () => void;
  // The FDIC overlay pill is on-screen from appbar-lg. Opt in so a backdrop/Escape dismiss can
  // confirm staged edits in that range. The always-on bar stays xl-only: it is CSS-hidden below
  // appbar-xl while still mounted, and must not steal click-away from the legacy EventBar.
  enableMenuCatchAll?: boolean;
};

const EventBarV2: React.FC<EventBarV2Props> = ({
  className,
  isCompressed = false,
  isVisible = true,
  onUpdateApplied,
  enableMenuCatchAll = false,
}) => {
  const {t} = useTranslation('app-bar');
  const [openField, setOpenField] = useState<EventBarField | null>(null);

  const {markFieldEngaged, openCommand} = useEventBarV2();
  const {trackFieldOpened, trackSearchButton, trackSearchIconHovered} = useEventBarV2Tracking();
  const {seenRef: preciseAddressNudgeRef} = usePreciseAddressNudge({trackExposure: isVisible});

  const trackFieldOpenedRef = useRef(trackFieldOpened);

  useEffect(() => {
    trackFieldOpenedRef.current = trackFieldOpened;
  }, [trackFieldOpened]);

  useEffect(() => {
    if (openField) trackFieldOpenedRef.current(openField);
  }, [openField]);

  // Opening a field is the ask: a user who looks at it and moves on has answered, so the bar stops
  // prompting for it. That holds for Where too — leaving a pre-filled address alone is a way of
  // accepting it, and an address that is actually missing or unresolvable is caught by the search
  // guard, which blocks the search and reopens Where with its banner. `what` is the field the user
  // types into, never one we prompt for.
  useEffect(() => {
    if (openField && openField !== 'what') markFieldEngaged(openField);
  }, [markFieldEngaged, openField]);

  // Consume external open commands, but only on the visible instance. The nonce is seeded on mount
  // so a bar that scrolls into view (and mounts) does not replay a command fired before it appeared.
  const lastHandledNonceRef = useRef(openCommand?.nonce ?? 0);

  useEffect(() => {
    if (!openCommand || !isVisible) return;
    if (openCommand.nonce === lastHandledNonceRef.current) return;
    lastHandledNonceRef.current = openCommand.nonce;
    setOpenField(openCommand.field);
  }, [openCommand, isVisible]);

  const {submitSearch, runSearchSubmission} = useEventBarV2Search();
  const {commitFulfillment} = useCommitEventBarFulfillment();
  const {whereSelection, beginSearchNavigation, hasUserEditedWhereRef} = useEventBarV2();
  const {isOnMenuPage} = useStoreNextRouter();
  const {isLoggedIn} = useIdentity();
  const pending = useMenuFulfillmentPending();
  const {decideAdvanceFrom} = useEventBarAskFlow();
  useRevertStagedOnFilterSearch();

  const {showEventBarFastFollows} = useEventBarFastFollows();

  const [confirmOpen, setConfirmOpen] = useState(false);
  // Set when the user leaves the bar and nothing is staged *yet*. A typed Where address only
  // becomes a `whereSelection` once its geocode resolves, which lands after the click that closed
  // the bar — so without this the first click-away sees no changes and the catch-all is skipped,
  // and the user has to click away a second time.
  const [awaitingPendingChanges, setAwaitingPendingChanges] = useState(false);

  const isAppBarXL = useMediaQuery(appBarMedia.xl);
  const menuCatchAllEnabled = isOnMenuPage && (enableMenuCatchAll || isAppBarXL);

  const isWhatOpen = openField === 'what';
  const isWhereOpen = openField === 'where';
  const isWhenOpen = openField === 'when';
  const isWhoOpen = openField === 'who';

  // A boolean setter for each field backed by the shared enum, so only one dropdown is open at a
  // time. Guards stale closes: a late onOpenChange(false) from a field that is no longer the open
  // one is ignored rather than closing whatever is currently open.
  const fieldOpenSetter = (field: EventBarField) => (open: boolean) =>
    setOpenField(prev => (open ? field : prev === field ? null : prev));

  // A field is done with: move to the next one the bar still needs to ask about, close the
  // dropdowns when nothing is left, or stand down on menu pages, which edit in place. Opening
  // another field implicitly closes this one — `openField` holds a single field.
  const advanceFrom = (from: EventBarField): AdvanceDecision => {
    const decision = decideAdvanceFrom(from);
    if (decision.kind === 'open') setOpenField(decision.field);
    else if (decision.kind === 'done') setOpenField(null);
    return decision;
  };

  // Expands the search button from an icon into a labeled "Search" pill; it collapses back to the
  // icon once the user leaves the bar — either by clicking off it or tabbing focus out of it.
  const [hasEngaged, setHasEngaged] = useState(false);
  // The fast-follows treatment pins the pill open instead.
  const showSearchLabel = hasEngaged || showEventBarFastFollows;
  // The field the traveling focus pill tracks. `activeField` is the live focus target; the pill
  // fades in on it and slides between fields. `displayedField` lags `activeField` so the pill stays
  // mounted (fading out) for a beat after focus leaves, then unmounts.
  const [activeField, setActiveField] = useState<EventBarField | null>(null);
  const [displayedField, setDisplayedField] = useState<EventBarField | null>(null);
  // The two fields the pill is currently sliding between. Both are marked `data-pill-connected` so
  // the merge (gray gap-fill + squared edges) holds for the slide instead of snapping to the
  // destination the instant focus lands. Held only during an adjacent slide, cleared when it ends.
  const [connectedFields, setConnectedFields] = useState<readonly EventBarField[]>([]);
  const shouldReduceMotion = useReducedMotion();
  const idSuffix = isCompressed ? 'compressed' : 'expanded';
  // Scope the pill's shared-layout id to this bar so the expanded and compressed bars each keep their
  // own pill — otherwise Motion FLIPs the one shared pill across the page when focus moves between them.
  const focusPillLayoutId = `event-bar-focus-pill-${idSuffix}`;

  // Highlight the hovered/focused field on its cell wrapper as a rounded pill, squaring any side
  // that abuts an adjacent active field so neighboring highlights merge into one shape. `data-pill-
  // connected` also counts a field as active while the pill slides to or from it, so the whole
  // merged shape — both fields' gray fill, the squared connecting edges, and the divider gap-fill —
  // holds for the length of the slide instead of snapping to the destination the instant focus lands.
  const cellHighlight = twJoin(
    '[&:is(:hover,:focus-within,[data-pill-connected])]:rounded-full',
    '[.peer:is(:hover,:focus-within,[data-pill-connected])_+_div_+_&]:rounded-l-none',
    '[&:has(+_div_+_.peer:is(:hover,:focus-within,[data-pill-connected]))]:rounded-r-none',
    hasEngaged
      ? '[&:is(:hover,:focus-within,[data-pill-connected])]:bg-peppercorn-50'
      : 'hover:bg-peppercorn-50',
  );

  // The "Where" component registers a guard that validates its address before a search.
  const searchGuardRef = useRef<() => Promise<SearchGuardResult>>(async () => ({ok: true}));
  const registerSearchGuard = useCallback((guard: () => Promise<SearchGuardResult>) => {
    searchGuardRef.current = guard;
  }, []);

  // One search at a time: a double-click, or Enter in Who followed by a click, would otherwise run
  // the whole validate-commit-search flow again while the first run is still awaiting. The ref is
  // the guard; the state only drives the button's disabled look.
  const isSearchingRef = useRef(false);
  const [isSearching, setIsSearching] = useState(false);

  const runExclusive = async (search: () => Promise<void>) => {
    if (isSearchingRef.current) return;
    isSearchingRef.current = true;
    setIsSearching(true);
    try {
      await search();
    } finally {
      isSearchingRef.current = false;
      setIsSearching(false);
    }
  };

  const handleSearch = () =>
    runExclusive(async () => {
      trackSearchButton();
      await runSearch();
    });

  // What searches on the spot when nothing is left to ask. It goes through the same Where check,
  // commit, and search tracking as the Search button, so a detail-less page still creates its
  // detail and the search is counted like any other.
  const handleWhatSearch = (override?: SubmitSearchOverride) =>
    runExclusive(async () => {
      trackSearchButton();
      await runSearch(override);
    });

  const runSearch = async (override?: SubmitSearchOverride) => {
    if (isOnMenuPage) {
      setOpenField(null);
      // A menu-page search navigates away; flush any staged Where/When/Who edits first so the
      // results reflect what the bar shows (mirrors the search-results path below). No-op when
      // nothing is staged. The commit may create the detail, so the search navigates to it — and
      // both of the menu page's URL syncs stand down, since either would cancel that navigation.
      beginSearchNavigation();
      const {createdFulfillmentDetailId} = await commitFulfillment(undefined, {skipUrlSync: true});
      await submitSearch(override, createdFulfillmentDetailId);
      return;
    }

    // A bad "Where" address opens the panel to show the error rather than searching.
    const guard = await searchGuardRef.current();
    if (!guard.ok) {
      setOpenField('where');
      return;
    }

    if (!isLoggedIn) recordRecentlyUsedAddress(guard.resolvedSelection ?? whereSelection);

    // The commit and search run as one submission, after the guard: on the search page the
    // submission holds the results in their loading state, so starting it before the guard would
    // flash the skeleton while a bad address geocodes, then restore the results without searching.
    await runSearchSubmission(async () => {
      // Field edits stage locally; flush them to the fulfillment detail once, here, so the search page
      // only updates on the search click — not when a popover closes. When the guard just resolved a
      // typed address, commit that selection directly — its state update isn't visible yet this tick.
      // On a page with no active detail, the commit creates one and returns its id so the search
      // navigates to it rather than a locationless search; an in-place update returns undefined.
      const {createdFulfillmentDetailId} = await commitFulfillment(guard.resolvedSelection);
      setOpenField(null);
      await submitSearch(override, createdFulfillmentDetailId);
    });
  };

  // Matches the bar itself and any of its portaled dropdowns; used to tell "still inside" from "left".
  // The dropdowns carry an app-owned `data-event-bar-surface` rather than a design-system attribute:
  // this used to match Radix's `[data-radix-popper-content-wrapper]`, which Base UI never emits, so
  // after the Tapas v8 migration the field panels only still counted as "inside" by accident (Base UI
  // gives its popups `role="dialog"`) and the corp-account Select — a listbox, not a dialog — didn't
  // count at all.
  // Includes any open dialog so clicks inside the menu-page catch-all modal (its Apply/Discard
  // buttons) don't count as leaving the bar and re-trigger the modal.
  const barPopoverSelector = `[data-testid="event-bar-v2-${idSuffix}"],[data-event-bar-surface],[role="dialog"]`;
  const isPointerDownInBar = useRef(false);
  useEffect(() => {
    const clear = () => (isPointerDownInBar.current = false);
    window.addEventListener('pointerup', clear);
    return () => window.removeEventListener('pointerup', clear);
  }, []);

  // Mirrors `displayedField` so `focusField` can read where the pill is leaving from (the slide's
  // start) without a stale state closure inside the event handler.
  const displayedFieldRef = useRef<EventBarField | null>(null);
  useEffect(() => {
    displayedFieldRef.current = displayedField;
  }, [displayedField]);

  // Hold the two fields visually merged while the pill slides between them, so the gray gap-fill and
  // squared connecting edge don't drop as focus moves. Adjacent moves only (nothing slides
  // otherwise), and skipped under reduced motion (the pill repositions instantly, no slide to cover).
  const holdMergeWhileSliding = (from: EventBarField | null, to: EventBarField) => {
    if (from && from !== to && !shouldReduceMotion && areAdjacentFields(from, to)) {
      setConnectedFields([from, to]);
    }
  };

  // Point the pill at a field (or clear it). Setting a field also advances `displayedField`
  // immediately, so the pill re-parents into the newly focused cell and slides there via layoutId.
  const focusField = (field: EventBarField | null) => {
    setActiveField(field);
    if (!field) return;
    setAwaitingPendingChanges(false);
    holdMergeWhileSliding(displayedFieldRef.current, field);
    setDisplayedField(field);
  };

  // Move the pill (and arm the merge) as the pointer goes down rather than on focus. Clicks that land
  // on a field's label padding rather than its input only move DOM focus on mouseup/click, so driving
  // this from onFocus alone would lag the pill behind the press; pointer-down also beats the
  // synchronous :focus-within flip so the leaving field never drops its highlight for a frame.
  // Keyboard moves have no pointer event and still drive the pill through onFocus.
  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    isPointerDownInBar.current = true;
    const target = fieldFromEventTarget(event.target);
    if (target) focusField(target);
  };

  // While any field's dropdown is open, keep the pill parented to that field rather than unmounting
  // it. Interacting inside the portaled panel (picking a date/time, an address suggestion, …) clears
  // activeField and fades the pill out, but the field it belongs to is still the one being edited —
  // so when selecting a value closes that panel and opens the next field's dropdown, the pill is
  // still mounted to slide (FLIP) across to it instead of popping in at the destination.
  const isAnyPopupOpen = isWhereOpen || isWhenOpen || isWhoOpen;

  // Once focus leaves every field (and no dropdown is holding the pill in place), keep the pill on
  // the last field long enough to fade out, then unmount it. (setState only runs inside the timeout,
  // so this doesn't trip set-state-in-effect.)
  useEffect(() => {
    if (activeField || isAnyPopupOpen) return;
    const timeout = setTimeout(() => setDisplayedField(null), PILL_FADE_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [activeField, isAnyPopupOpen]);

  // Release the held connection once the pill's slide completes (matching FocusPill), so the merge
  // relaxes back to reflecting live hover/focus. (setState only runs inside the timeout.)
  useEffect(() => {
    if (connectedFields.length === 0) return;
    const timeout = setTimeout(() => setConnectedFields([]), PILL_SLIDE_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [connectedFields]);

  const handleLeaveBar = useCallback(() => {
    setHasEngaged(false);
    setActiveField(null);

    if (!menuCatchAllEnabled || confirmOpen) return;

    if (pending.hasChanges) {
      setConfirmOpen(true);
      return;
    }
    // Nothing staged yet, but a Where resolution may still be in flight — the effect below opens
    // the catch-all if it settles into a real change.
    if (hasUserEditedWhereRef.current && !whereSelection) setAwaitingPendingChanges(true);
  }, [confirmOpen, hasUserEditedWhereRef, menuCatchAllEnabled, pending.hasChanges, whereSelection]);

  // Escape is a leave-bar path, same as click-away. A field popup consumes Escape first; once no
  // popup is open, pending menu-page edits open the catch-all.
  useEffect(() => {
    if (!isVisible || !menuCatchAllEnabled) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || isAnyPopupOpen) return;
      handleLeaveBar();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleLeaveBar, isAnyPopupOpen, isVisible, menuCatchAllEnabled]);

  // Opens the catch-all for a change that resolves just after the user left the bar. Armed only by
  // `handleLeaveBar`, so a resolution that reverts to the committed address — `hasChanges` never
  // becoming true — correctly stays silent. (setState only runs inside the timeout.)
  useEffect(() => {
    if (!awaitingPendingChanges || !menuCatchAllEnabled || confirmOpen) return;
    if (!pending.hasChanges) return;
    const timeout = setTimeout(() => {
      setConfirmOpen(true);
      setAwaitingPendingChanges(false);
    }, 0);
    return () => clearTimeout(timeout);
  }, [awaitingPendingChanges, menuCatchAllEnabled, confirmOpen, pending.hasChanges]);

  const handleClickAway = (event: FocusEvent | MouseEvent | TouchEvent) => {
    if (!isVisible) return;
    if (event.type !== 'click') return;
    const target = event.target as HTMLElement | null;
    if (target?.closest(barPopoverSelector)) return;
    handleLeaveBar();
  };

  // Collapse when focus leaves the bar via the keyboard (Tab). relatedTarget is where focus is
  // headed: keep expanded if it's still inside the bar or an open dropdown, otherwise collapse.
  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!isVisible) return;
    if (isPointerDownInBar.current) return;
    const nextTarget = event.relatedTarget as HTMLElement | null;
    // Still inside the bar (or an open dropdown): keep the bar engaged and move the pill to
    // whichever field now holds focus — null when focus lands in a portaled panel, which fades the
    // pill out just as the old green focus border did.
    if (!nextTarget || nextTarget.closest(barPopoverSelector)) {
      focusField(fieldFromEventTarget(nextTarget));
      return;
    }
    handleLeaveBar();
  };

  // Presence attribute (empty string when set, absent otherwise) the cellHighlight/Divider selectors
  // read to treat a field as active while the pill is mid-slide to or from it.
  const pillConnected = (field: EventBarField) =>
    connectedFields.includes(field) ? '' : undefined;

  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <div
          ref={preciseAddressNudgeRef}
          data-testid={`event-bar-v2-${idSuffix}`}
          className={twMerge(
            'flex max-w-pageContent items-center justify-center overflow-hidden rounded-full border',
            isCompressed ? 'shadow-event-bar-sm' : 'shadow-event-bar-lg',
            className,
          )}
          onBlur={handleBlur}
          onFocus={event => {
            setHasEngaged(true);
            focusField(fieldFromEventTarget(event.target));
          }}
          onPointerDown={handlePointerDown}
        >
          {/* When the Where panel opens, What shrinks and Where grows. The `delay` in
            CELL_RESIZE_TRANSITION_CLASSES holds that resize until the focus pill finishes sliding
            into Where, so the pill slides into the not-yet-widened cell and then grows with it. */}
          <div
            data-event-bar-field="what"
            data-pill-connected={pillConnected('what')}
            className={twJoin(
              // Keeps the resize smooth when Where opens: min-w-0 lets all cells shrink at once
              // (no one-at-a-time min-content jitter), and What/Where trade space on a constant
              // combined basis so When/Who stay put.
              'peer relative min-w-0',
              CELL_RESIZE_TRANSITION_CLASSES,
              cellHighlight,
              isCompressed &&
                !isWhereOpen &&
                'basis-1/3 [&:has(+_div_+_.peer:focus-within)]:basis-1/5',
              isCompressed && isWhereOpen && 'basis-1/5',
              !isCompressed &&
                !isWhereOpen &&
                'basis-2/5 [&:has(+_div_+_.peer:focus-within)]:basis-1/3',
              !isCompressed && isWhereOpen && 'basis-1/3',
            )}
          >
            {displayedField === 'what' && (
              <FocusPill active={activeField === 'what'} layoutId={focusPillLayoutId} />
            )}
            <What
              isCompressed={isCompressed}
              isOpen={isWhatOpen}
              setIsOpen={fieldOpenSetter('what')}
              onAdvance={() => advanceFrom('what')}
              onSearch={handleWhatSearch}
            />
          </div>
          <Divider isCompressed={isCompressed} isHidden={isWhereOpen} />
          <div
            data-event-bar-field="where"
            data-pill-connected={pillConnected('where')}
            className={twJoin(
              'peer relative min-w-0',
              CELL_RESIZE_TRANSITION_CLASSES,
              cellHighlight,
              isCompressed && !isWhereOpen && 'basis-1/3 focus-within:basis-2/5',
              isCompressed && isWhereOpen && 'basis-2/5',
              !isCompressed && !isWhereOpen && 'basis-1/3 focus-within:basis-2/5',
              !isCompressed && isWhereOpen && 'basis-2/5',
            )}
            data-testid="where-field"
          >
            {displayedField === 'where' && (
              <FocusPill active={activeField === 'where'} layoutId={focusPillLayoutId} />
            )}
            <Where
              isCompressed={isCompressed}
              isOpen={isWhereOpen}
              onUpdateApplied={onUpdateApplied}
              setIsOpen={fieldOpenSetter('where')}
              onAdvance={() => advanceFrom('where')}
              registerSearchGuard={registerSearchGuard}
            />
          </div>
          <Divider isCompressed={isCompressed} isHidden={isWhereOpen} />
          <div
            data-event-bar-field="when"
            data-pill-connected={pillConnected('when')}
            className={twJoin('peer relative min-w-0 basis-1/3', cellHighlight)}
          >
            {displayedField === 'when' && (
              <FocusPill active={activeField === 'when'} layoutId={focusPillLayoutId} />
            )}
            <When
              isCompressed={isCompressed}
              isOpen={isWhenOpen}
              onUpdateApplied={onUpdateApplied}
              setIsOpen={fieldOpenSetter('when')}
              onAdvance={() => advanceFrom('when')}
            />
          </div>
          <Divider isCompressed={isCompressed} />
          <div
            data-event-bar-field="who"
            data-pill-connected={pillConnected('who')}
            className={twJoin('peer relative min-w-0 basis-1/4', cellHighlight)}
          >
            {displayedField === 'who' && (
              <FocusPill active={activeField === 'who'} layoutId={focusPillLayoutId} />
            )}
            <Who
              isCompressed={isCompressed}
              isOpen={isWhoOpen}
              onSearch={handleSearch}
              onUpdateApplied={onUpdateApplied}
              setIsOpen={fieldOpenSetter('who')}
            />
          </div>
          <div className={'ml-2 flex shrink-0 grow-0 basis-[122px] justify-end'}>
            <Button
              aria-label={t('eventBarV2.search')}
              className={twMerge(
                'flex items-center justify-center rounded-full p-0',
                SEARCH_BUTTON_TRANSITION_CLASSES,
                isCompressed ? 'mr-3 size-9' : 'mr-4 size-12',
                showSearchLabel && 'w-auto px-6',
              )}
              data-search-trigger
              data-testid="event-bar-v2-search-button"
              loading={isSearching}
              // Keep focus on the button while it's disabled mid-search, so keyboard users aren't
              // dropped back to the page.
              focusableWhenDisabled
              onClick={handleSearch}
              onMouseEnter={trackSearchIconHovered}
            >
              {showSearchLabel ? (
                <span className="whitespace-nowrap">{t('eventBarV2.search')}</span>
              ) : (
                <Icon className="no-underline" icon={faMagnifyingGlass} size="xsmall" />
              )}
            </Button>
          </div>
        </div>
      </ClickAwayListener>
      {menuCatchAllEnabled && (
        <MenuUpdateConfirmationModal isOpen={confirmOpen} onOpenChange={setConfirmOpen} />
      )}
    </>
  );
};

export default EventBarV2;
